
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, User, UserPlus, Settings, FolderOpen, ArrowRight } from "lucide-react";
import { ClienteSearchSelect } from "@/components/ClienteSearchSelect";
import { EquipamentosSelector } from "@/components/orcamentos/EquipamentosSelector";
import { useProjetos } from "@/hooks/useProjetos";
import { supabase } from "@/integrations/supabase/client";

interface DadosCliente {
  nome: string;
  email: string;
  telefone: string;
  observacoes: string;
}

interface OrcamentoClienteFormProps {
  tipoCliente: string;
  setTipoCliente: (value: string) => void;
  clienteSelecionado: string;
  clienteNomeSelecionado: string;
  handleClienteChange: (clienteId: string, clienteNome: string) => void;
  dadosCliente: DadosCliente;
  setDadosCliente: (dados: DadosCliente) => void;
  gerarOrcamento: () => void;
  qtdPaineis: number;
  isPending: boolean;
  orcamentoId?: string;
  onProjetoSelect?: (projetoId: string, qtdPaineis?: number) => void;
}

export function OrcamentoClienteForm({
  tipoCliente,
  setTipoCliente,
  clienteSelecionado,
  clienteNomeSelecionado,
  handleClienteChange,
  dadosCliente,
  setDadosCliente,
  gerarOrcamento,
  qtdPaineis,
  isPending,
  orcamentoId,
  onProjetoSelect
}: OrcamentoClienteFormProps) {
  const { data: projetos = [] } = useProjetos();
  
  // Filtrar projetos do cliente selecionado
  const projetosDoCliente = projetos.filter(p => 
    clienteSelecionado && p.cliente_id === parseInt(clienteSelecionado)
  );
  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-gray-900">
          <FileText className="h-5 w-5 text-green-600" />
          Dados do Cliente
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Tipo de Cliente</Label>
          <RadioGroup value={tipoCliente} onValueChange={setTipoCliente} className="mt-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="existente" id="existente" />
              <Label htmlFor="existente" className="flex items-center gap-2 cursor-pointer">
                <User className="h-4 w-4 text-green-600" />
                Cliente Existente
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="teste" id="teste" />
              <Label htmlFor="teste" className="flex items-center gap-2 cursor-pointer">
                <UserPlus className="h-4 w-4 text-blue-600" />
                Cliente Teste
              </Label>
            </div>
          </RadioGroup>
        </div>

        {tipoCliente === "existente" ? (
          <ClienteSearchSelect
            value={clienteSelecionado}
            onChange={handleClienteChange}
            label="Buscar Cliente"
          />
        ) : (
          <>
            <div>
              <Label htmlFor="nome">Nome Completo</Label>
              <Input
                id="nome"
                value={dadosCliente.nome}
                onChange={(e) => setDadosCliente({...dadosCliente, nome: e.target.value})}
                placeholder="Nome do cliente"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={dadosCliente.email}
                onChange={(e) => setDadosCliente({...dadosCliente, email: e.target.value})}
                placeholder="email@exemplo.com"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="telefone">Telefone</Label>
              <Input
                id="telefone"
                value={dadosCliente.telefone}
                onChange={(e) => setDadosCliente({...dadosCliente, telefone: e.target.value})}
                placeholder="(00) 00000-0000"
                className="mt-1"
              />
            </div>
          </>
        )}
        
        {/* Projetos Existentes do Cliente */}
        {tipoCliente === "existente" && clienteSelecionado && projetosDoCliente.length > 0 && (
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <FolderOpen className="h-5 w-5" />
                Projetos Existentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm text-blue-700 mb-3">
                  Este cliente possui {projetosDoCliente.length} projeto(s). Deseja vincular o orçamento a algum?
                </p>
                {projetosDoCliente.map((projeto) => (
                  <div key={projeto.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        Projeto {projeto.id}
                      </div>
                      <div className="text-sm text-gray-600">
                        Status: {projeto.status} • {new Date(projeto.created_at).toLocaleDateString('pt-BR')}
                        {projeto.orcamento_id && (
                          <span className="ml-2 text-green-600">• Já possui orçamento</span>
                        )}
                      </div>
                    </div>
                    {!projeto.orcamento_id ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          // Buscar equipamentos do projeto para contar painéis
                          const { data: equipamentos } = await supabase
                            .from('projeto_equipamentos')
                            .select('quantidade, equipamento:equipamentos(tipo)')
                            .eq('projeto_id', projeto.id);
                          
                          const totalPaineis = equipamentos
                            ?.filter(eq => eq.equipamento?.tipo?.toLowerCase() === 'painel')
                            .reduce((total, eq) => total + eq.quantidade, 0) || 0;
                          
                          onProjetoSelect?.(projeto.id.toString(), totalPaineis);
                        }}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      >
                        <ArrowRight className="h-4 w-4 mr-1" />
                        Vincular
                      </Button>
                    ) : (
                      <span className="text-sm text-gray-500">Orçamento já criado</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Projetos sem Orçamento */}
        {tipoCliente === "existente" && clienteSelecionado && (
          <Card className="bg-green-50 border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-800">
                <FileText className="h-5 w-5" />
                Criar Orçamento para Projeto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm text-green-700 mb-3">
                  Selecione um projeto para criar um orçamento com diferentes planos:
                </p>
                {projetosDoCliente.filter(p => !p.orcamento_id).map((projeto) => (
                  <div key={projeto.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        Projeto {projeto.id}
                      </div>
                      <div className="text-sm text-gray-600">
                        Status: {projeto.status} • {new Date(projeto.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        // Buscar equipamentos do projeto para contar painéis
                        const { data: equipamentos } = await supabase
                          .from('projeto_equipamentos')
                          .select('quantidade, equipamento:equipamentos(tipo)')
                          .eq('projeto_id', projeto.id);
                        
                        const totalPaineis = equipamentos
                          ?.filter(eq => (eq.equipamento as any)?.tipo?.toLowerCase() === 'painel')
                          .reduce((total, eq) => total + eq.quantidade, 0) || 0;
                        
                        onProjetoSelect?.(projeto.id.toString(), totalPaineis);
                      }}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      <ArrowRight className="h-4 w-4 mr-1" />
                      Criar Orçamento
                    </Button>
                  </div>
                ))}
                {projetosDoCliente.filter(p => !p.orcamento_id).length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    Todos os projetos deste cliente já possuem orçamentos.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div>
          <Label htmlFor="observacoes">Observações</Label>
          <Textarea
            id="observacoes"
            value={dadosCliente.observacoes}
            onChange={(e) => setDadosCliente({...dadosCliente, observacoes: e.target.value})}
            placeholder="Observações adicionais..."
            className="mt-1"
          />
        </div>

        {/* Seleção de Equipamentos */}
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="h-5 w-5 text-blue-600" />
            <Label className="text-base font-semibold">Equipamentos do Projeto</Label>
          </div>
          <EquipamentosSelector orcamentoId={orcamentoId} />
        </div>

        <Button 
          onClick={gerarOrcamento} 
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
          disabled={!qtdPaineis || (tipoCliente === "existente" && !clienteSelecionado) || isPending}
        >
          {isPending ? "Gerando..." : "Gerar Orçamento"}
        </Button>
      </CardContent>
    </Card>
  );
}
